import { useEffect, useMemo } from "react";
import type { DocumentLook, ThemeId } from "@/lib/api";
import { buildCanvasLook, effectiveTheme, normaliseLook, type CanvasLook } from "@/lib/themes/look";
import { loadThemeFont, themeFontStack } from "@/lib/themes/fonts";
import { useTheme } from "@/hooks/useThemes";
import { useThemeStore } from "@/store/themeStore";
import { MODE_DARK } from "@/constants/theme.constant";

/** Where the per-heading rules apply: the Flow canvas. */
export const CANVAS_SCOPE = '[data-testid="flow-editor"]';

interface BlockLike {
  id: string;
  type: string;
  content?: Record<string, unknown> | null;
}

/**
 * The document's look as the canvas and the outline use it, or null for
 * Classic (or while the descriptor loads). Loads the theme's heading font the
 * first time a document needs it — never for a Classic document.
 */
export function useCanvasLook(
  look: DocumentLook | null | undefined,
  lookLocked: string | null | undefined,
  blocks: readonly BlockLike[],
  lookThemes?: readonly ThemeId[] | null,
): CanvasLook | null {
  // A class that does not allow the stored theme prints Classic (the look is kept for later).
  const l = effectiveTheme(look, lookLocked, lookThemes) === "classic" ? null : normaliseLook(look);
  const theme = useTheme(l?.theme);
  const headingName = theme?.fonts.heading;
  const headingCss = theme?.fonts.headingCss;
  // The editor's Dark theme: a coloured paper shows as its dark-paper pair.
  const dark = useThemeStore((s) => s.mode) === MODE_DARK;

  useEffect(() => {
    if (headingName) void loadThemeFont(headingName);
  }, [headingName]);

  return useMemo(
    () => (l ? buildCanvasLook(l, theme, blocks, themeFontStack(headingName, headingCss), CANVAS_SCOPE, dark) : null),
    // `l` is rebuilt every render; its parts are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [l?.theme, l?.paper, l ? JSON.stringify(l.pins) : "", theme, blocks, headingName, headingCss, dark],
  );
}
